import { useState } from 'react';
import { Alert, Button, Group, Paper, SegmentedControl, Slider, Stack, Switch, Text } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { DownloadSimpleIcon, ImageIcon, TrashIcon, WarningIcon } from '@phosphor-icons/react';
import {
  type ConvertResult,
  EXT,
  type OutputFormat,
  convertImage,
  formatBytes,
  loadImage,
} from '@/lib/image-convert';
import { useStrings } from '@/i18n/useLang';

export function ImgConverter() {
  const t = useStrings();
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [sourceName, setSourceName] = useState('image');
  const [format, setFormat] = useState<OutputFormat>('webp');
  const [quality, setQuality] = useState(85);
  const [resize, setResize] = useState(false);
  const [maxWidth, setMaxWidth] = useState(1024);
  const [result, setResult] = useState<ConvertResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (image: HTMLImageElement) => {
    setBusy(true);
    setError(null);
    try {
      setResult(
        await convertImage(image, { format, quality: quality / 100, maxWidth: resize ? maxWidth : 0 }),
      );
    } catch {
      setError(t.ui.error);
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (file: File) => {
    try {
      const image = await loadImage(file);
      setImg(image);
      setSourceName(file.name.replace(/\.[^.]+$/, '') || 'image');
      await run(image);
    } catch {
      setError(t.ui.error);
    }
  };

  const reconvert = () => img && run(img);

  const reset = () => {
    if (result) URL.revokeObjectURL(result.url);
    setImg(null);
    setResult(null);
    setError(null);
    setSourceName('image');
  };

  return (
    <Stack gap="lg">
      {!img ? (
        <Dropzone onDrop={(files) => files[0] && onFile(files[0])} accept={IMAGE_MIME_TYPE} multiple={false} loading={busy} radius="md">
          <Group justify="center" gap="sm" mih={110} style={{ pointerEvents: 'none' }}>
            <ImageIcon size={32} color="var(--primary)" />
            <div>
              <Text fw={600}>{t.ui.dropTitle}</Text>
              <Text size="sm" c="dimmed">
                {t.ui.dropHint}
              </Text>
            </div>
          </Group>
        </Dropzone>
      ) : (
        <Paper withBorder radius="md" p="sm" style={{ borderColor: 'var(--border-light)' }}>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" style={{ minWidth: 0 }}>
              <ImageIcon size={20} color="var(--primary)" />
              <Text size="sm" truncate>
                {sourceName}
              </Text>
            </Group>
            <Button variant="subtle" color="red" size="xs" leftSection={<TrashIcon size={16} />} onClick={reset}>
              {t.ui.remove}
            </Button>
          </Group>
        </Paper>
      )}

      <div>
        <Text fw={500} size="sm" mb={6}>
          {t.ui.format}
        </Text>
        <SegmentedControl
          fullWidth
          value={format}
          onChange={(v) => setFormat(v as OutputFormat)}
          data={[
            { value: 'webp', label: 'WebP' },
            { value: 'png', label: 'PNG' },
            { value: 'jpeg', label: 'JPEG' },
          ]}
        />
      </div>

      {format !== 'png' && (
        <div>
          <Group justify="space-between" mb={4}>
            <Text fw={500} size="sm">
              {t.ui.quality}
            </Text>
            <Text fw={700} c="cyan" size="sm">
              {quality}
            </Text>
          </Group>
          <Slider min={10} max={100} value={quality} onChange={setQuality} aria-label={t.ui.quality} />
        </div>
      )}

      <Switch label={t.ui.resize} checked={resize} onChange={(e) => setResize(e.currentTarget.checked)} />
      {resize && (
        <div>
          <Group justify="space-between" mb={4}>
            <Text fw={500} size="sm">
              {t.ui.maxWidth}
            </Text>
            <Text fw={700} c="cyan" size="sm">
              {maxWidth}px
            </Text>
          </Group>
          <Slider min={64} max={4096} step={64} value={maxWidth} onChange={setMaxWidth} aria-label={t.ui.maxWidth} />
        </div>
      )}

      {img && (
        <Button onClick={reconvert} loading={busy} variant="light">
          {t.ui.convert}
        </Button>
      )}

      {error && (
        <Alert color="red" icon={<WarningIcon size={18} />} variant="light">
          {error}
        </Alert>
      )}

      {result && !busy && (
        <Paper withBorder radius="md" p="md" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-light)' }}>
          <Stack gap="md" align="center">
            <img src={result.url} alt="" style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 8 }} />
            <Text size="sm" c="dimmed">
              {result.width}×{result.height} · {formatBytes(result.bytes)}
            </Text>
            <Button
              component="a"
              href={result.url}
              download={`${sourceName}.${EXT[format]}`}
              leftSection={<DownloadSimpleIcon size={18} />}
            >
              {t.ui.download} (.{EXT[format]})
            </Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
